import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';

interface ErrorDialogContextType {
  showAccessDeniedDialog: () => void;
  showErrorDialog: (message: string) => void;
  showSuccessDialog: (message: string) => void;
}

const ErrorDialogContext = createContext<ErrorDialogContextType | undefined>(undefined);

export const useErrorDialog = (): ErrorDialogContextType => {
  const context = useContext(ErrorDialogContext);
  if (!context) {
    throw new Error('useErrorDialog must be used within an ErrorDialogProvider');
  }
  return context;
};

interface ErrorDialogProviderProps {
  children: ReactNode;
}

export const ErrorDialogProvider: React.FC<ErrorDialogProviderProps> = ({ children }) => {
  const showAccessDeniedDialog = () => {
    const event = new CustomEvent('accessDenied');
    window.dispatchEvent(event);
  };

  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const showErrorDialog = (message: string) => {
    setErrorMessage(message);
    setErrorSnackbarOpen(true);
  };

  const showSuccessDialog = (message: string) => {
    setSuccessMessage(message);
    setSuccessSnackbarOpen(true);
  };

  const handleErrorSnackbarClose = () => {
    setErrorSnackbarOpen(false);
  };

  const handleSuccessSnackbarClose = () => {
    setSuccessSnackbarOpen(false);
  };

  useEffect(() => {
    const handleApiError = (event: CustomEvent) => {
      showErrorDialog(event.detail);
    };

    const handleApiSuccess = (event: CustomEvent) => {
      showSuccessDialog(event.detail);
    };

    window.addEventListener('apiError', handleApiError as EventListener);
    window.addEventListener('apiSuccess', handleApiSuccess as EventListener);

    return () => {
      window.removeEventListener('apiError', handleApiError as EventListener);
      window.removeEventListener('apiSuccess', handleApiSuccess as EventListener);
    };
  }, []);

  return (
    <ErrorDialogContext.Provider value={{ showAccessDeniedDialog, showErrorDialog, showSuccessDialog }}>
      {children}
      <Snackbar open={errorSnackbarOpen} autoHideDuration={6000} onClose={handleErrorSnackbarClose}>
        <Alert onClose={handleErrorSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={successSnackbarOpen} autoHideDuration={6000} onClose={handleSuccessSnackbarClose}>
        <Alert onClose={handleSuccessSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </ErrorDialogContext.Provider>
  );
};